// =============================================================================
// CHECKOUT EXTENSIONS ZOD SCHEMAS
// =============================================================================
// Re-export all schemas and types
// =============================================================================
// MAIN SCHEMAS (with proper circular reference resolution)
// =============================================================================
import { z } from 'zod';
import { GlobalActionSchema } from './actions.js';
import { GenericExtensionConditionsSchema } from './conditions.js';
import { ExtensionI18nStringSchema, ExtensionIdSchema, ExtensionLocaleSchema } from './extension.js';
import { ExtensionFieldSchema } from './fields.js';
import { ZFulfillmentConstraintRuleSchema } from './fulfillment-constraints.js';
export * from './extension.js';
export * from './conditions.js';
export * from './actions.js';
export * from './fields.js';
export * from './fulfillment-constraints.js';
export * from './helpers/index.js';
// =============================================================================
// BASE SCHEMA (shared properties)
// =============================================================================
export const BaseCheckoutExtensionSchema = z.object({
    id: ExtensionIdSchema,
    defaultLocale: ExtensionLocaleSchema,
    title: ExtensionI18nStringSchema.optional().refine((value) => value && Object.values(value).some((v) => typeof v === 'string' && v.length > 0), { message: 'Title must have at least one non-empty locale' }),
    description: ExtensionI18nStringSchema.optional(),
    enabled: z.boolean(),
});
// =============================================================================
// CONTEXT-SPECIFIC SCHEMAS
// =============================================================================
/**
 * Global Extension Schema
 * - Must have at least one action
 * - Cannot have fields (no UI components in global context)
 * - Used for automated workflows triggered by events
 */
export const GlobalCheckoutExtensionSchema = BaseCheckoutExtensionSchema.extend({
    context: z.literal('global'),
    actions: z.array(GlobalActionSchema).min(1, 'Global extensions must have at least one action'),
    when: GenericExtensionConditionsSchema.optional(),
}).strict();
/**
 * UI Extension Schema
 */
export const UICheckoutExtensionSchema = BaseCheckoutExtensionSchema.extend({
    context: z.literal('below-shipping'),
    fields: z.array(ExtensionFieldSchema).optional(),
    when: GenericExtensionConditionsSchema.optional(),
}).strict();
/**
 * Fulfillment Constraint Extension Schema
 * - Carries blocking `rules` (no `actions`, no `fields`).
 * - Backend-only: enforced before payment in the checkout service; the
 *   checkout-form `ExtensionManager` ignores this context (it renders/executes nothing).
 * - The warehouse source location(s) used by the rules live globally on
 *   `shop.settings.fulfillment.locations.warehouse`, not on the extension.
 *
 * @example
 * const extension: FulfillmentConstraintCheckoutExtension = {
 *   id: generateExtensionId(),
 *   title: { en: 'Boutique-only stock protection' },
 *   defaultLocale: 'en',
 *   enabled: true,
 *   context: 'fulfillment-constraint',
 *   rules: [
 *     {
 *       id: generateExtensionRuleId(),
 *       enabled: true,
 *       target: 'collection',
 *       collectionIds: ['gid://shopify/Collection/288766591065'],
 *     },
 *   ],
 * };
 */
export const FulfillmentConstraintCheckoutExtensionSchema = BaseCheckoutExtensionSchema.extend({
    context: z.literal('fulfillment-constraint'),
    rules: z
        .array(ZFulfillmentConstraintRuleSchema)
        .min(1, 'Fulfillment-constraint extensions must have at least one rule'),
}).strict();
// =============================================================================
// MAIN DISCRIMINATED UNION SCHEMA
// =============================================================================
/**
 * Complete Checkout Extension Schema
 * Automatically validates the correct structure based on context value
 */
export const CheckoutExtensionSchema = z.discriminatedUnion('context', [
    UICheckoutExtensionSchema,
    GlobalCheckoutExtensionSchema,
    FulfillmentConstraintCheckoutExtensionSchema,
]);
//# sourceMappingURL=index.js.map